import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FolderOpen, Settings, CreditCard, LogOut, ChevronDown, Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const UserSidebar = ({ appSettings, onBuyCredits, sidebarOpen = false, setSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [upcomingFeatures, setUpcomingFeatures] = useState([]);

  const activePath = location.pathname;

  // Load upcoming features from appSettings prop
  useEffect(() => {
    if (appSettings && appSettings.upcoming_features) {
      // Convert upcoming_features object to array
      const featuresArray = Object.values(appSettings.upcoming_features).sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      setUpcomingFeatures(featuresArray);
    } else {
      setUpcomingFeatures([]);
    }
  }, [appSettings]);

  const Item = ({ icon: Icon, label, active, onClick, disabled = false, badge = null }) => (
    <button
      type="button"
      className="nav-item"
      title={label}
      onClick={(e) => { if (!disabled && onClick) onClick(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!disabled && onClick) onClick(); } }}
      disabled={disabled}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 10,
        border: active ? '1px solid var(--color-nav-item-active-border)' : '1px solid transparent',
        background: active ? 'var(--color-nav-item-active-bg)' : 'transparent',
        color: disabled ? 'var(--color-nav-fg-muted)' : 'var(--color-nav-fg)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon size={18} />
        <span className="nav-label" style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      </div>
      {badge && (
        <span className="nav-label" style={{
          fontSize: 10,
          fontWeight: 600,
          padding: '2px 6px',
          borderRadius: 8,
          backgroundColor: badge === 'SOON' ? 'var(--color-warning)' : 'var(--color-danger)',
          color: 'var(--color-text-inverse)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {badge}
        </span>
      )}
    </button>
  );

  const avatarChar = ((user?.full_name || user?.email || 'U').trim()[0] || 'U').toUpperCase();

  return (
    <aside
      className={`mobile-sidebar ${sidebarOpen ? 'open' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: 'var(--layout-sidebar-width)',
        background: 'var(--gradient-nav)',
        color: 'var(--color-nav-fg)',
        padding: 16,
        boxShadow: 'var(--color-nav-edge)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Mobile Close Button */}
      <div className="mobile-header" style={{ display: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {appSettings?.product_logo ? (
              <img src={appSettings.product_logo} alt="Logo" style={{ height: 24, objectFit: 'contain' }} />
            ) : (
              <div style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--color-nav-surface-strong)' }} />
            )}
            <div style={{ fontSize: 16, fontWeight: 800 }}>{appSettings?.product_name || 'Product'}</div>
          </div>
          <button
            onClick={() => setSidebarOpen && setSidebarOpen(false)}
            style={{ background: 'none', border: 'none', color: 'var(--color-nav-fg)', padding: '4px', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Brand - Desktop */}
      <div className="desktop-brand" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        {appSettings?.product_logo ? (
          <img src={appSettings.product_logo} alt="Logo" style={{ height: 28, objectFit: 'contain' }} />
        ) : (
          <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--color-nav-surface-strong)' }} />
        )}
        <div className="nav-brand-name" style={{ fontSize: 16, fontWeight: 800 }}>{appSettings?.product_name || 'Product'}</div>
      </div>

      <div className="nav-section-label" style={{ fontSize: 12, letterSpacing: 0.4, color: 'var(--color-nav-section-fg)', margin: '6px 0 10px' }}>TOOLS</div>

      {/* Scrapers Section */}
      <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
        <Item
          icon={Search}
          label="Apollo Leads"
          active={activePath === '/dashboard'}
          onClick={() => navigate('/dashboard')}
        />

        {/* Enabled & Ready Features (clickable - enabled AND not showing as coming soon) */}
        {upcomingFeatures
          .filter(feature => feature.enabled && feature.show_coming_soon === false)
          .map((feature) => {
            // All features use dedicated routes (kebab-case URLs)
            const routePath = `/dashboard/${feature.name.replace(/_/g, '-')}`;
            return (
              <Item
                key={feature.name}
                icon={() => <span style={{ fontSize: 16 }}>{feature.icon}</span>}
                label={feature.display_name}
                active={activePath === routePath}
                onClick={() => navigate(routePath)}
              />
            );
          })}

        {/* Coming Soon Features (show "SOON" badge - either disabled OR enabled but marked as coming soon) */}
        {upcomingFeatures
          .filter(feature => (!feature.enabled || feature.show_coming_soon === true) && feature.show_coming_soon !== false)
          .map((feature) => (
            <Item
              key={feature.name}
              icon={() => <span style={{ fontSize: 16 }}>{feature.icon}</span>}
              label={feature.display_name}
              active={false}
              disabled={true}
              badge="SOON"
              onClick={() => {}}
            />
          ))}
      </div>

      <div className="nav-section-label" style={{ fontSize: 12, letterSpacing: 0.4, color: 'var(--color-nav-section-fg)', margin: '6px 0 10px' }}>ACCOUNT</div>

      {/* Nav */}
      <div style={{ display: 'grid', gap: 8 }}>
        <Item
          icon={CreditCard}
          label="Buy Credits"
          active={false}
          onClick={() => onBuyCredits && onBuyCredits()}
        />
        <Item
          icon={Settings}
          label="Settings"
          active={activePath.startsWith('/dashboard/settings')}
          onClick={() => navigate('/dashboard/settings/profile')}
        />
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* User menu (bottom) */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--color-nav-border)',
            background: 'var(--color-nav-surface)',
            color: 'var(--color-nav-fg)',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--color-nav-surface-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
              {avatarChar}
            </div>
            <div style={{ textAlign: 'left', minWidth: 0 }}>
              <div className="nav-user-meta" style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name || user?.email || 'User'}</div>
              <div className="nav-user-meta" style={{ fontSize: 11, opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
            </div>
          </div>
          <ChevronDown size={16} style={{ transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }} />
        </button>

        {menuOpen && (
          <div style={{
            position: 'absolute',
            bottom: '48px',
            left: 0,
            right: 0,
            background: 'var(--color-nav-surface)',
            border: '1px solid var(--color-nav-border)',
            borderRadius: 10,
            overflow: 'hidden',
            zIndex: 60
          }}>
            <Item icon={LogOut} label="Logout" active={false} onClick={() => logout()} />
          </div>
        )}
      </div>
    </aside>
  );
};

export default UserSidebar;